// Espace Brand Ambassador et page de gestion partagée (CEO, administrateurs, comptable,
// secrétariat, Community Manager). Toutes les données et tous les calculs viennent du serveur.
import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import downloadFile from "../../services/downloadFile";
import { imprimerDocument } from "../../services/imprimer";
import { qlAlert, qlConfirm } from "../../utils/dialogs";
import DocumentViewer from "../DocumentViewer";
import { Chargement, Chip, Entete, Fenetre, Kpi, Ligne, Message } from "../compta/ComptaEspace";
import { MULTIPART, dateHeure, erreurApi, formulaire, ouvrirPage } from "../community/cmOutils";
import "../compta/ComptaEspace.css";
import "../secretariat/SecretariatEspace.css";
import "./AmbassadeurEspace.css";

const TONS = { navy: "#071d50", blue: "#2464e8", gold: "#c58f00", green: "#15803d", red: "#e64049" };
const TON_VENTE = { non_payee: "grey", partielle: "", en_retard: "red", soldee: "green", annulee: "grey" };
const TON_ETAT = { disponible: "green", reserve: "amber", remis: "", retourne: "grey", endommage: "red", perdu: "red" };
const MODES = [["especes", "Espèces"], ["mtn_momo", "MTN Mobile Money"], ["orange_money", "Orange Money"]];
const CLASSES = ["Terminale C", "Terminale D", "Terminale TI", "Première C", "Première D", "Première TI", "Seconde C", "Autre"];

export const fcfa = (n) => `${new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0))} FCFA`;
const jour = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR") : "—");
const nouvelleCle = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Impression d'un PDF produit par le serveur (navigateur, téléphone et applications).
export function imprimer(url, nom) {
  return api.get(url, { responseType: "blob" })
    .then((r) => imprimerDocument({ data: r.data, filename: nom }))
    .catch(() => qlAlert("Impression impossible.", "error"));
}

// Chaque document imprimable est aussi téléchargeable, et inversement.
export function BoutonsDocument({ url, nom, lien = false, libelle = "" }) {
  const classe = lien ? "qc-link" : "qc-btn qc-btn--sm";
  return (
    <span className="qa-doc">
      <button type="button" className={classe} onClick={() => imprimer(url, nom)} aria-label={`Imprimer ${libelle || nom}`}>
        <i className="bi bi-printer"></i>{lien ? " " : ""}Imprimer{libelle ? ` ${libelle}` : ""}</button>
      <button type="button" className={classe} onClick={() => telecharger(url, nom)} aria-label={`Télécharger ${libelle || nom}`}>
        <i className="bi bi-download"></i>{lien ? " " : ""}Télécharger</button>
    </span>
  );
}

// Téléchargement d'un PDF produit par le serveur (fonctionne aussi dans les applications).
export function telecharger(url, nom) {
  const mimeType = "application/pdf";
  return api.get(url, { responseType: "blob" })
    .then((r) => downloadFile({ url: `${api.defaults.baseURL}generated/${encodeURIComponent(nom)}`, filename: nom, mimeType, data: r.data }))
    .then((r) => { if (!r?.cancelled) qlAlert("Document téléchargé.", "success"); })
    .catch(() => qlAlert("Téléchargement impossible.", "error"));
}

function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.pages <= 1) return null;
  return (
    <div className="qs-pagination" role="navigation" aria-label="Pagination">
      <button className="qc-btn qc-btn--sm" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>← Précédent</button>
      <span>Page {pagination.page} sur {pagination.pages} · {pagination.total} résultats</span>
      <button className="qc-btn qc-btn--sm" disabled={pagination.page >= pagination.pages} onClick={() => onPage(pagination.page + 1)}>Suivant →</button>
    </div>
  );
}

function Progression({ paye, prix }) {
  const pct = prix ? Math.min(100, Math.round((100 * Math.max(0, paye)) / prix)) : 0;
  return <div className="qa-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>;
}

// ── Versement d'une tranche ──────────────────────────────────────────────────

function FormVersement({ vente, onClose, onSaved }) {
  const [cle] = useState(nouvelleCle);
  const [valeurs, setValeurs] = useState({ montant: String(Math.min(vente.reste, vente.echeances?.find((e) => !e.reglee)?.montant || vente.reste)), mode: "especes", reference: "" });
  const [fichier, setFichier] = useState(null);
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const maj = (k) => (e) => setValeurs((v) => ({ ...v, [k]: e.target.value }));
  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setMessage("");
    const donnees = formulaire({ ...valeurs, cle_idempotence: cle });
    if (fichier) donnees.append("justificatif", fichier);
    try {
      const r = await api.post(`ambassadeur/ventes/${vente.id}/versements/`, donnees, MULTIPART);
      onSaved(r.data);
    } catch (err) { setMessage(erreurApi(err)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre={`Versement · ${vente.eleve}`} onClose={onClose}>
      <form onSubmit={envoyer}>
        <Ligne label="Reste à payer" valeur={fcfa(vente.reste)} />
        <Ligne label="Tranches encore possibles" valeur={vente.tranches_restantes ?? "—"} />
        <div className="qc-form" style={{ marginTop: 12 }}>
          <label>Montant reçu (FCFA)<input type="number" min="1" max={vente.reste} required value={valeurs.montant} onChange={maj("montant")} /></label>
          <label>Moyen de paiement<select value={valeurs.mode} onChange={maj("mode")}>{MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label className="qc-full">{valeurs.mode === "especes" ? "Référence (facultatif)" : "Numéro de transaction Mobile Money"}
            <input value={valeurs.reference} onChange={maj("reference")} required={valeurs.mode !== "especes"} maxLength={120} /></label>
          <label className="qc-full">Justificatif (photo ou PDF, facultatif)
            <input type="file" accept="application/pdf,image/*" onChange={(e) => setFichier(e.target.files?.[0] || null)} /></label>
        </div>
        {valeurs.mode !== "especes" && <div className="qs-notice">Un paiement Mobile Money saisi ici reste « déclaré » jusqu’à son rapprochement par le comptable.</div>}
        <Message texte={message} />
        <div className="qc-actions">
          <button type="button" className="qc-btn" onClick={onClose}>Annuler</button>
          <button type="submit" className="qc-btn qc-btn--gold" disabled={envoi}>{envoi ? "Enregistrement…" : "Enregistrer et créer le reçu"}</button>
        </div>
      </form>
    </Fenetre>
  );
}

// ── Fiche d'une vente ────────────────────────────────────────────────────────

export function FicheVente({ id, comptable = false, onClose, onChange }) {
  const [vente, setVente] = useState(null);
  const [message, setMessage] = useState("");
  const [versement, setVersement] = useState(false);
  const [piece, setPiece] = useState(null);
  const charger = useCallback(() => {
    api.get(`ambassadeur/ventes/${id}/`).then((r) => setVente(r.data)).catch(() => setVente(false));
  }, [id]);
  useEffect(charger, [charger]);
  const action = async (nom, extra = {}) => {
    setMessage("");
    try { const r = await api.post(`ambassadeur/ventes/${id}/action/`, { action: nom, ...extra }); setVente(r.data); onChange?.(); }
    catch (err) { setMessage(erreurApi(err)); }
  };
  const corriger = (v) => {
    const motif = window.prompt(`Motif de la contre-écriture du reçu ${v.numero_recu} :`);
    if (!motif) return;
    api.post(`ambassadeur/versements/${v.id}/corriger/`, { motif }).then(() => { charger(); onChange?.(); })
      .catch((err) => setMessage(erreurApi(err)));
  };
  const rapprocher = (v) => api.post(`ambassadeur/versements/${v.id}/rapprocher/`).then(charger).catch((err) => setMessage(erreurApi(err)));
  const rappel = () => {
    const note = window.prompt("Note du rappel (appel, message au parent…) :", "");
    if (note !== null) action("rappel", { canal: "telephone", note });
  };
  const annuler = () => qlConfirm("Annuler cette vente ? L’exemplaire revient dans le stock.", () => {
    const motif = window.prompt("Motif de l’annulation :") || "";
    action("annuler", { motif, retour: true });
  }, { danger: true, okLabel: "Annuler la vente" });

  if (versement) return <FormVersement vente={vente} onClose={() => setVersement(false)} onSaved={() => { setVersement(false); charger(); onChange?.(); }} />;
  return (
    <Fenetre titre={vente ? `Vente ${vente.numero}` : "Vente"} onClose={onClose}>
      {vente === null ? <Chargement /> : vente === false ? <div className="qc-empty">Vente introuvable.</div> : <>
        <h4 className="qs-fiche-titre">{vente.eleve}</h4>
        <div className="qs-chips">
          <Chip ton={TON_VENTE[vente.statut]}>{vente.statut_label}</Chip>
          <Chip ton={vente.remis ? "green" : "amber"}>{vente.remis ? "Manuel remis" : "Réservé, non remis"}</Chip>
          <Chip ton={vente.active ? "green" : "grey"}>{vente.active ? "Code activé" : "Code non activé"}</Chip>
        </div>
        <dl className="qs-details">
          <dt>Établissement</dt><dd>{vente.etablissement} — {vente.classe}</dd>
          <dt>Manuel</dt><dd>{vente.manuel}</dd>
          <dt>Code manuel</dt><dd className="qa-code">{vente.code}</dd>
          {vente.contact_tuteur && <><dt>Parent / tuteur</dt><dd>{vente.contact_tuteur}</dd></>}
          <dt>Prix</dt><dd>{fcfa(vente.prix)} en {vente.nb_tranches} tranche{vente.nb_tranches > 1 ? "s" : ""}</dd>
          <dt>Versé / reste</dt><dd>{fcfa(vente.paye)} / <strong>{fcfa(vente.reste)}</strong></dd>
          {comptable && <><dt>Ambassadeur</dt><dd>{vente.ambassadeur.nom}</dd></>}
        </dl>
        <Progression paye={vente.paye} prix={vente.prix} />
        <div className="qc-section-title">Échéancier</div>
        <div className="qa-echeances">
          {vente.echeances.map((e) => (
            <div key={e.rang} className={`qa-echeance${e.reglee ? " qa-echeance--ok" : ""}`}>
              <strong>Tranche {e.rang}</strong><span>{fcfa(e.montant)}</span><small>{jour(e.date)} · {e.reglee ? "réglée" : "à payer"}</small>
            </div>
          ))}
        </div>
        <div className="qc-section-title">Versements et reçus</div>
        {vente.versements.length === 0 ? <div className="qc-foot">Aucun versement pour le moment.</div> : (
          <div className="qc-table-wrap">
            <table className="qc-table">
              <thead><tr><th>Reçu</th><th>Montant</th><th>Moyen</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {vente.versements.map((v) => (
                  <tr key={v.id} className={v.annule || v.contre_ecriture ? "qa-barre" : ""}>
                    <td><strong>{v.numero_recu}</strong>{v.contre_ecriture && <small>Contre-écriture : {v.motif}</small>}{v.annule && <small>Annulé</small>}</td>
                    <td>{fcfa(v.montant)}</td>
                    <td>{v.mode_label}{v.mobile && <> <Chip ton={v.rapproche ? "green" : "amber"}>{v.rapproche ? "Rapproché" : "Déclaré"}</Chip></>}
                      {v.reference && <small>Réf. {v.reference}</small>}</td>
                    <td>{dateHeure(v.date)}</td>
                    <td className="qa-actions-ligne">
                      <BoutonsDocument lien url={`ambassadeur/versements/${v.id}/recu/`} nom={`${v.numero_recu}.pdf`} libelle="le reçu" />
                      {v.justificatif_url && <button className="qc-link" onClick={() => setPiece(v.justificatif_url)}>Justificatif</button>}
                      {comptable && v.mobile && !v.rapproche && !v.contre_ecriture && <button className="qc-link" onClick={() => rapprocher(v)}>Rapprocher</button>}
                      {comptable && v.montant > 0 && !v.annule && <button className="qc-link qa-danger" onClick={() => corriger(v)}>Contre-écriture</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {piece && <DocumentViewer titre="Justificatif" url={piece} onClose={() => setPiece(null)} />}
        {vente.rappels.length > 0 && <>
          <div className="qc-section-title">Rappels</div>
          <ul className="qc-histo">{vente.rappels.map((r, i) => <li key={i}><strong>{r.note || "Rappel"}</strong> <span>— {r.auteur}, {dateHeure(r.date)}</span></li>)}</ul>
        </>}
        <Message texte={message} />
        {!vente.annulee && (
          <div className="qc-actions qs-actions">
            {comptable && <button className="qc-btn qc-btn--danger" onClick={annuler}>Annuler la vente</button>}
            {!comptable && !vente.remis && <button className="qc-btn" onClick={() => action("remettre")}>Manuel remis à l’élève</button>}
            {!comptable && vente.reste > 0 && <button className="qc-btn" onClick={rappel}>Noter un rappel</button>}
            {vente.reste > 0 && vente.tranches_restantes > 0 && (
              <button className="qc-btn qc-btn--gold" onClick={() => setVersement(true)}>Enregistrer un versement</button>
            )}
          </div>
        )}
      </>}
    </Fenetre>
  );
}

// ── Registre des ventes (ambassadeur, ou comptable pour un ambassadeur) ─────

export function RegistreVentes({ ambassadeurId = null, statutInitial = "", titre = "Registre des ventes", comptable = false, action }) {
  const [donnees, setDonnees] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [statut, setStatut] = useState(statutInitial);
  const [page, setPage] = useState(1);
  const [fiche, setFiche] = useState(null);
  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams({ page: String(page), ...extra });
    if (ambassadeurId) p.set("ambassadeur", ambassadeurId);
    if (statut) p.set("statut", statut);
    if (recherche.trim()) p.set("q", recherche.trim());
    return p;
  }, [page, ambassadeurId, statut, recherche]);
  const charger = useCallback(() => {
    api.get(`ambassadeur/ventes/?${params()}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false));
  }, [params]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  useEffect(() => { setPage(1); }, [statut, recherche, ambassadeurId]);
  return (
    <div className="qc-card">
      <div className="qs-card-head">
        <h3>{titre}</h3>
        <div className="qa-boutons">
          <BoutonsDocument url={`ambassadeur/ventes/?${params({ export: "pdf" })}`} nom="releve-ventes.pdf" />
          {action}
        </div>
      </div>
      <div className="qc-toolbar">
        <input className="qc-search qs-grow" placeholder="Rechercher élève, école, classe ou code" value={recherche} onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher" />
        <select value={statut} onChange={(e) => setStatut(e.target.value)} aria-label="État">
          <option value="">Toutes les ventes</option>
          <option value="a_recouvrer">Avec un reste à payer</option>
          <option value="en_retard">En retard</option>
          <option value="partielle">Partielles</option>
          <option value="non_payee">Non payées</option>
          <option value="soldee">Soldées</option>
          <option value="annulee">Annulées</option>
        </select>
      </div>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Chargement impossible.</div> : <>
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Élève / établissement</th><th>Classe</th><th>Code manuel</th><th>Prix</th><th>Versé</th><th>Reste</th><th>État</th><th></th></tr></thead>
            <tbody>
              {donnees.ventes.map((v) => (
                <tr key={v.id}>
                  <td><strong>{v.eleve}</strong><small>{v.etablissement}{comptable && !ambassadeurId ? ` · ${v.ambassadeur.nom}` : ""}</small></td>
                  <td>{v.classe}</td><td className="qa-code">{v.code}</td><td>{fcfa(v.prix)}</td><td>{fcfa(v.paye)}</td>
                  <td>{fcfa(v.reste)}<Progression paye={v.paye} prix={v.prix} /></td>
                  <td><Chip ton={TON_VENTE[v.statut]}>{v.statut_label}</Chip></td>
                  <td><button className="qc-link" onClick={() => setFiche(v.id)}>Ouvrir</button></td>
                </tr>
              ))}
              {!donnees.ventes.length && <tr><td colSpan={8} className="qc-empty">{recherche || statut ? "Aucune vente ne correspond." : "Aucune vente enregistrée."}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="qc-foot">{donnees.totaux.ventes} vente(s) · montant total {fcfa(donnees.totaux.prix)}</div>
        <Pagination pagination={donnees.pagination} onPage={setPage} />
      </>}
      {fiche && <FicheVente id={fiche} comptable={comptable} onClose={() => setFiche(null)} onChange={charger} />}
    </div>
  );
}

// ── Pages de l'ambassadeur ───────────────────────────────────────────────────

function useTableau() {
  const [donnees, setDonnees] = useState(null);
  const charger = useCallback(() => { api.get("ambassadeur/tableau/").then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, []);
  useEffect(charger, [charger]);
  return [donnees, charger];
}

export function AMBDashboardPage({ user }) {
  const [d] = useTableau();
  const prenom = (user?.nom_complet || "").split(" ")[0];
  return (
    <div className="qc qs qa">
      <Entete titre={`Bonjour${prenom ? ` ${prenom}` : ""} 👋`} sousTitre="Suivez vos ventes et vos encaissements.">
        <button className="qc-btn qc-btn--gold" onClick={() => ouvrirPage("amb_vente")}>＋ Nouvelle vente</button>
      </Entete>
      {d === null ? <Chargement /> : d === false ? <div className="qc-empty">Tableau de bord indisponible.</div> : <>
        <div className="qc-kpis">
          <Kpi label="📚 Manuels reçus" value={d.recus} ton={TONS.navy} sub={`${d.par_etat.disponible} disponible(s)`} />
          <Kpi label="🛍️ Manuels vendus" value={d.vendus} ton={TONS.blue} sub={`${d.remis} remis à des élèves`} />
          <Kpi label="💰 Encaissements" value={fcfa(d.encaisse)} ton={TONS.green} sub={d.mobile_non_rapproche ? `dont ${fcfa(d.mobile_non_rapproche)} à rapprocher` : "Versements des élèves"} />
          <Kpi label="⏳ Soldes à recouvrer" value={fcfa(d.reste_a_recouvrer)} ton={TONS.gold} sub={`${d.en_retard} élève(s) en retard`} />
        </div>
        <div className="qc-cols">
          <div className="qc-card">
            <div className="qs-card-head"><h3>Dernières ventes</h3><button className="qc-btn qc-btn--sm" onClick={() => ouvrirPage("amb_ventes")}>Tout afficher →</button></div>
            <div className="qc-table-wrap">
              <table className="qc-table">
                <thead><tr><th>Élève</th><th>Classe</th><th>Payé</th><th>Reste</th></tr></thead>
                <tbody>
                  {d.dernieres_ventes.map((v) => <tr key={v.id}><td>{v.eleve}</td><td>{v.classe}</td><td>{fcfa(v.paye)}</td><td>{fcfa(v.reste)}</td></tr>)}
                  {!d.dernieres_ventes.length && <tr><td colSpan={4} className="qc-empty">Aucune vente pour le moment.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <div className="qc-card">
              <h3>Actions rapides</h3>
              <div className="qs-quick">
                <button onClick={() => ouvrirPage("amb_vente")}><i className="bi bi-bag-plus"></i>Nouvelle vente →</button>
                <button onClick={() => ouvrirPage("amb_versements")}><i className="bi bi-cash-coin"></i>Enregistrer un versement →</button>
                <button onClick={() => ouvrirPage("amb_stock")}><i className="bi bi-box-seam"></i>Voir mon stock →</button>
                <button onClick={() => ouvrirPage("amb_reversements")}><i className="bi bi-bank"></i>Reverser à la comptabilité →</button>
              </div>
            </div>
            <div className="qc-card" style={{ marginTop: 16 }}>
              <h3>⏰ Retards de paiement</h3>
              {d.retards.map((v) => <div key={v.id} className="qs-item"><span className="qs-dot" /><div><strong>{v.eleve}</strong><small>{v.classe} · reste {fcfa(v.reste)}</small></div></div>)}
              {!d.retards.length && <div className="qc-foot">Aucun retard. 👍</div>}
              <div className="qc-note">À reverser à la comptabilité : <strong>{fcfa(d.a_reverser)}</strong></div>
            </div>
          </div>
        </div>
      </>}
    </div>
  );
}

export function AMBNouvelleVentePage() {
  const [stock, setStock] = useState(null);
  const [valeurs, setValeurs] = useState({ exemplaire: "", eleve_nom: "", etablissement: "", classe: CLASSES[0], contact_tuteur: "", consentement_tuteur: false,
    nb_tranches: "3", dates: ["", "", ""], remis: false, premier_montant: "", premier_mode: "especes", premier_reference: "" });
  const [cle, setCle] = useState(nouvelleCle);
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [fiche, setFiche] = useState(null);
  const chargerStock = useCallback(() => {
    api.get("ambassadeur/stock/?etat=disponible").then((r) => setStock(r.data.exemplaires)).catch(() => setStock([]));
  }, []);
  useEffect(chargerStock, [chargerStock]);
  const maj = (k) => (e) => setValeurs((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const nb = Number(valeurs.nb_tranches);
  const choisi = stock?.find((s) => String(s.id) === String(valeurs.exemplaire));
  const enregistrer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setMessage("");
    const corps = { ...valeurs, dates: valeurs.dates.slice(0, nb), premier_cle_idempotence: cle };
    if (!corps.premier_montant) { delete corps.premier_montant; }
    try {
      const r = await api.post("ambassadeur/ventes/", corps);
      if (r.data.avertissement) qlAlert(r.data.avertissement, "warning");
      setFiche(r.data.vente.id);
      setValeurs((v) => ({ ...v, exemplaire: "", eleve_nom: "", contact_tuteur: "", consentement_tuteur: false, premier_montant: "", premier_reference: "" }));
      setCle(nouvelleCle());
      chargerStock();
    } catch (err) { setMessage(erreurApi(err)); } finally { setEnvoi(false); }
  };
  return (
    <div className="qc qs qa">
      <Entete titre="Nouvelle vente" sousTitre="Identifiez l’élève et le manuel de votre stock." />
      <div className="qs-notice">Le prix est celui fixé par la direction. La remise physique du manuel et l’activation du code par l’élève sont deux étapes distinctes.</div>
      <form className="qc-card" onSubmit={enregistrer}>
        <div className="qc-form">
          <label className="qc-full">Exemplaire (code manuel de votre stock) *
            <select required value={valeurs.exemplaire} onChange={maj("exemplaire")}>
              <option value="">{stock === null ? "Chargement…" : stock.length ? "Choisir un code" : "Aucun exemplaire disponible dans votre stock"}</option>
              {(stock || []).map((s) => <option key={s.id} value={s.id}>{s.code} — {s.manuel.titre}</option>)}
            </select></label>
          <label>Nom et prénom de l’élève *<input required maxLength={120} value={valeurs.eleve_nom} onChange={maj("eleve_nom")} placeholder="Ex. Nadia M." /></label>
          <label>Établissement *<input required maxLength={200} value={valeurs.etablissement} onChange={maj("etablissement")} placeholder="Ex. Lycée de …" /></label>
          <label>Classe *<select value={valeurs.classe} onChange={maj("classe")}>{CLASSES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label>Paiement en<select value={valeurs.nb_tranches} onChange={maj("nb_tranches")}>
            <option value="1">1 tranche (comptant)</option><option value="2">2 tranches</option><option value="3">3 tranches</option></select></label>
          {Array.from({ length: nb }, (_, i) => (
            <label key={i}>Date de la tranche {i + 1} *
              <input type="date" required value={valeurs.dates[i]} onChange={(e) => setValeurs((v) => { const d = [...v.dates]; d[i] = e.target.value; return { ...v, dates: d }; })} /></label>
          ))}
          <label>Téléphone du parent ou tuteur (facultatif)<input maxLength={120} value={valeurs.contact_tuteur} onChange={maj("contact_tuteur")} /></label>
          <label className="qa-check"><input type="checkbox" checked={valeurs.consentement_tuteur} onChange={maj("consentement_tuteur")} />
            Le parent ou tuteur accepte que ce contact soit enregistré</label>
          <label className="qa-check"><input type="checkbox" checked={valeurs.remis} onChange={maj("remis")} />Manuel remis à l’élève dès maintenant</label>
          <div className="qc-full qc-section-title">Premier versement (facultatif)</div>
          <label>Montant (FCFA)<input type="number" min="1" value={valeurs.premier_montant} onChange={maj("premier_montant")} /></label>
          <label>Moyen de paiement<select value={valeurs.premier_mode} onChange={maj("premier_mode")}>{MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          {valeurs.premier_mode !== "especes" && <label className="qc-full">Numéro de transaction<input value={valeurs.premier_reference} onChange={maj("premier_reference")} /></label>}
        </div>
        {choisi && <div className="qc-foot">Manuel : {choisi.manuel.titre}. Les montants des tranches sont calculés par le serveur à partir du prix officiel.</div>}
        <Message texte={message} />
        <div className="qc-actions"><button className="qc-btn qc-btn--gold" type="submit" disabled={envoi}>{envoi ? "Enregistrement…" : "Enregistrer la vente"}</button></div>
      </form>
      {fiche && <FicheVente id={fiche} onClose={() => setFiche(null)} />}
    </div>
  );
}

export function AMBVentesPage() {
  return (
    <div className="qc qs qa">
      <Entete titre="Mes ventes" sousTitre="Retrouvez toutes les ventes enregistrées, imprimez votre relevé.">
        <button className="qc-btn qc-btn--gold" onClick={() => ouvrirPage("amb_vente")}>＋ Nouvelle vente</button>
      </Entete>
      <RegistreVentes />
    </div>
  );
}

export function AMBVersementsPage() {
  return (
    <div className="qc qs qa">
      <Entete titre="Versements" sousTitre="Enregistrez les paiements échelonnés (trois tranches au plus) et imprimez les reçus." />
      <div className="qs-notice">Chaque versement est lié à une vente, ne peut pas dépasser le solde et reçoit un reçu numéroté. Une erreur se corrige par la comptabilité, jamais par une suppression.</div>
      <RegistreVentes titre="Ventes avec un reste à payer" statutInitial="a_recouvrer" />
    </div>
  );
}

export function AMBRecouvrementsPage() {
  return (
    <div className="qc qs qa">
      <Entete titre="Recouvrements" sousTitre="Identifiez les soldes restant à payer et notez vos rappels." />
      <RegistreVentes titre="Élèves en retard de paiement" statutInitial="en_retard" />
    </div>
  );
}

export function StockAmbassadeur({ ambassadeurId = null, gestion = false, ambassadeurs = [], onChange }) {
  const [donnees, setDonnees] = useState(null);
  const [etat, setEtat] = useState("");
  const [recherche, setRecherche] = useState("");
  const [page, setPage] = useState(1);
  const base = ambassadeurId ? `ambassadeurs/${ambassadeurId}/stock/` : "ambassadeur/stock/";
  const charger = useCallback(() => {
    const p = new URLSearchParams({ page: String(page) });
    if (etat) p.set("etat", etat);
    if (recherche.trim()) p.set("q", recherche.trim());
    api.get(`${base}?${p}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false));
  }, [base, page, etat, recherche]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  useEffect(() => { setPage(1); }, [etat, recherche, ambassadeurId]);
  const agir = (ex, corps) => api.post(`ambassadeurs/exemplaires/${ex.id}/action/`, corps).then(() => { charger(); onChange?.(); })
    .catch((err) => qlAlert(erreurApi(err), "error"));
  const retirer = (ex) => qlConfirm(`Retirer le code ${ex.code} de la liste de ${ex.ambassadeur.nom} ? Il redeviendra libre.`,
    () => agir(ex, { action: "retirer" }), { danger: true, okLabel: "Retirer" });
  const transferer = (ex) => {
    const autres = ambassadeurs.filter((a) => a.id !== ex.ambassadeur.id);
    const choix = window.prompt(`Transférer ${ex.code} vers :\n${autres.map((a, i) => `${i + 1}. ${a.nom}`).join("\n")}\n\nNuméro :`);
    const cible = autres[Number(choix) - 1];
    if (cible) agir(ex, { action: "transferer", ambassadeur: cible.id });
  };
  const p = donnees?.par_etat || {};
  return (
    <div className="qc-card">
      <div className="qs-card-head">
        <h3>Manuels confiés {donnees?.ambassadeur ? `à ${donnees.ambassadeur.nom}` : ""}</h3>
        <BoutonsDocument url={`${base}?export=pdf`} nom="manuels-confies.pdf" libelle="la liste" />
      </div>
      <div className="qa-etats">
        {(donnees?.etats || []).map(([k, l]) => <span key={k}><Chip ton={TON_ETAT[k]}>{l}</Chip> {p[k] || 0}</span>)}
      </div>
      <div className="qc-toolbar">
        <input className="qc-search qs-grow" placeholder="Rechercher un code ou un manuel" value={recherche} onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher" />
        <select value={etat} onChange={(e) => setEtat(e.target.value)} aria-label="État"><option value="">Tous les états</option>
          {(donnees?.etats || []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </div>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Chargement impossible.</div> : <>
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Code manuel</th><th>Manuel</th><th>État physique</th><th>État numérique</th><th>Liste</th>{gestion && <th>Actions</th>}</tr></thead>
            <tbody>
              {donnees.exemplaires.map((e) => (
                <tr key={e.id}>
                  <td className="qa-code">{e.code}</td><td>{e.manuel.titre}{e.vente && <small>{e.vente.eleve} · {e.vente.classe}</small>}</td>
                  <td><Chip ton={TON_ETAT[e.etat]}>{e.etat_label}</Chip></td>
                  <td>{e.active ? `Activé le ${dateHeure(e.active_le)}` : "Non activé"}</td>
                  <td>{e.dotation || "—"}</td>
                  {gestion && <td className="qa-actions-ligne">{!e.vendu && <>
                    <button className="qc-link" onClick={() => transferer(e)}>Transférer</button>
                    <button className="qc-link qa-danger" onClick={() => retirer(e)} title="Code saisi par erreur">Retirer</button>
                    <select aria-label="Changer l’état" value="" onChange={(ev) => ev.target.value && agir(e, { action: "etat", etat: ev.target.value })}>
                      <option value="">État…</option><option value="disponible">Disponible</option><option value="retourne">Retourné</option>
                      <option value="endommage">Endommagé</option><option value="perdu">Perdu</option></select></>}</td>}
                </tr>
              ))}
              {!donnees.exemplaires.length && <tr><td colSpan={gestion ? 6 : 5} className="qc-empty">Aucun manuel dans cette liste.</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination pagination={donnees.pagination} onPage={setPage} />
      </>}
    </div>
  );
}

export function AMBStockPage() {
  return (
    <div className="qc qs qa">
      <Entete titre="Stock de manuels" sousTitre="La liste de vos manuels est établie par la direction, la comptabilité, le secrétariat ou la CM : elle est la même pour tous." />
      <StockAmbassadeur />
    </div>
  );
}

export function AMBReversementsPage() {
  const [donnees, setDonnees] = useState(null);
  const [form, setForm] = useState(false);
  const charger = useCallback(() => { api.get("ambassadeur/reversements/").then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, []);
  useEffect(charger, [charger]);
  const b = donnees?.bilan;
  return (
    <div className="qc qs qa">
      <Entete titre="Reversements" sousTitre="Remettez les sommes encaissées à Quick Learning ; la comptabilité valide chaque bordereau.">
        <button className="qc-btn qc-btn--gold" onClick={() => setForm(true)} disabled={!b?.a_reverser}>＋ Nouveau bordereau</button>
      </Entete>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Chargement impossible.</div> : <>
        <div className="qc-cols">
          <div className="qc-card">
            <h3>Encaissements et reversements</h3>
            <Ligne label="Encaissements enregistrés" valeur={fcfa(b.encaisse)} />
            <Ligne label="Reversements validés" valeur={fcfa(b.reverse_valide)} classe="qc-green" />
            <Ligne label="Reversements en attente de validation" valeur={fcfa(b.reverse_soumis)} />
            <Ligne label="Reste à reverser" valeur={fcfa(b.a_reverser)} classe="qc-amber" />
            {b.mobile_non_rapproche > 0 && <div className="qc-note">Dont {fcfa(b.mobile_non_rapproche)} de paiements Mobile Money encore à rapprocher par le comptable.</div>}
          </div>
          <div className="qc-card">
            <h3>Comment faire ?</h3>
            <ol className="qa-etapes">
              <li>Enregistrez chaque versement d’élève et donnez-lui son reçu.</li>
              <li>Remettez l’argent à Quick Learning et créez un bordereau avec la preuve.</li>
              <li>La comptabilité valide le bordereau ; il entre dans les revenus.</li>
            </ol>
          </div>
        </div>
        <div className="qc-card">
          <h3>Mes bordereaux</h3>
          <TableReversements reversements={donnees.reversements} />
        </div>
      </>}
      {form && <FormReversement maximum={b.a_reverser} onClose={() => setForm(false)} onSaved={() => { setForm(false); charger(); }} />}
    </div>
  );
}

function TableReversements({ reversements, onDecision }) {
  const [piece, setPiece] = useState(null);
  return (
    <div className="qc-table-wrap">
      <table className="qc-table">
        <thead><tr><th>Bordereau</th>{onDecision && <th>Ambassadeur</th>}<th>Montant</th><th>Moyen</th><th>État</th><th>Date</th><th></th></tr></thead>
        <tbody>
          {reversements.map((r) => (
            <tr key={r.id}>
              <td><strong>{r.numero}</strong>{r.reference && <small>Réf. {r.reference}</small>}</td>
              {onDecision && <td>{r.ambassadeur.nom}</td>}
              <td>{fcfa(r.montant)}</td><td>{r.mode_label}</td>
              <td><Chip ton={{ soumis: "amber", valide: "green", rejete: "red" }[r.statut]}>{r.statut_label}</Chip>{r.motif && <small>{r.motif}</small>}</td>
              <td>{dateHeure(r.created_at)}</td>
              <td className="qa-actions-ligne">
                {r.justificatif_url && <button className="qc-link" onClick={() => setPiece(r.justificatif_url)}>Preuve</button>}
                {onDecision && r.statut === "soumis" && <>
                  <button className="qc-link" onClick={() => onDecision(r, "valider")}>Valider</button>
                  <button className="qc-link qa-danger" onClick={() => onDecision(r, "rejeter")}>Rejeter</button></>}
              </td>
            </tr>
          ))}
          {!reversements.length && <tr><td colSpan={onDecision ? 7 : 6} className="qc-empty">Aucun bordereau.</td></tr>}
        </tbody>
      </table>
      {piece && <DocumentViewer titre="Preuve de reversement" url={piece} onClose={() => setPiece(null)} />}
    </div>
  );
}

function FormReversement({ maximum, onClose, onSaved }) {
  const [valeurs, setValeurs] = useState({ montant: String(maximum), mode: "especes", reference: "" });
  const [fichier, setFichier] = useState(null);
  const [message, setMessage] = useState("");
  const maj = (k) => (e) => setValeurs((v) => ({ ...v, [k]: e.target.value }));
  const envoyer = async (e) => {
    e.preventDefault();
    const donnees = formulaire(valeurs);
    if (fichier) donnees.append("justificatif", fichier);
    try { await api.post("ambassadeur/reversements/", donnees, MULTIPART); onSaved(); } catch (err) { setMessage(erreurApi(err)); }
  };
  return (
    <Fenetre titre="Nouveau bordereau de reversement" onClose={onClose}>
      <form onSubmit={envoyer}>
        <div className="qc-form">
          <label>Montant reversé (FCFA)<input type="number" min="1" max={maximum} required value={valeurs.montant} onChange={maj("montant")} /></label>
          <label>Moyen<select value={valeurs.mode} onChange={maj("mode")}>{[...MODES, ["virement", "Virement"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
          <label className="qc-full">Référence (n° de transaction, de reçu…)<input value={valeurs.reference} onChange={maj("reference")} /></label>
          <label className="qc-full">Preuve (photo ou PDF)<input type="file" accept="application/pdf,image/*" onChange={(e) => setFichier(e.target.files?.[0] || null)} /></label>
        </div>
        <Message texte={message} />
        <div className="qc-actions"><button className="qc-btn qc-btn--gold" type="submit">Soumettre à la comptabilité</button></div>
      </form>
    </Fenetre>
  );
}

export function AMBPerformancesPage() {
  const [d] = useTableau();
  const max = Math.max(1, ...(d?.ventes_par_mois || []).map((m) => m.ventes));
  return (
    <div className="qc qs qa">
      <Entete titre="Performances" sousTitre="Suivez le nombre de manuels vendus.">
        <BoutonsDocument url="ambassadeur/ventes/?export=pdf" nom="releve-ventes.pdf" libelle="mon relevé" />
      </Entete>
      {!d ? <Chargement /> : <>
        <div className="qc-kpis">
          <Kpi label="Manuels vendus" value={d.vendus} ton={TONS.navy} sub="Ventes non annulées" />
          <Kpi label="Manuels remis" value={d.remis} ton={TONS.blue} sub="Aux élèves" />
          <Kpi label="Encaissé" value={fcfa(d.encaisse)} ton={TONS.green} sub="Versements des élèves" />
          <Kpi label="Stock disponible" value={d.par_etat.disponible} ton={TONS.gold} sub={`sur ${d.recus} reçus`} />
        </div>
        <div className="qc-card">
          <h3>Ventes par mois</h3>
          {d.ventes_par_mois.length === 0 ? <div className="qc-empty">Pas encore de vente.</div> : d.ventes_par_mois.map((m) => (
            <div key={m.mois} className="qa-barre-mois"><span>{m.mois}</span><div className="qa-progress"><i style={{ width: `${(100 * m.ventes) / max}%` }} /></div><strong>{m.ventes}</strong></div>
          ))}
          <div className="qc-foot">Les commissions éventuelles sont calculées uniquement selon un barème validé par la direction.</div>
        </div>
      </>}
    </div>
  );
}

// ── Gestion des ambassadeurs (CEO, administrateurs, comptable, secrétariat, CM) ──

function FormDotation({ ambassadeurs, manuels, ambassadeurInitial, onClose, onSaved }) {
  const [valeurs, setValeurs] = useState({ ambassadeur: ambassadeurInitial || ambassadeurs[0]?.id || "", mode: "quantite", manuel: manuels[0]?.id || "",
    quantite: "10", codes: "", note: "" });
  const [libres, setLibres] = useState(null);
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const maj = (k) => (e) => setValeurs((v) => ({ ...v, [k]: e.target.value }));
  useEffect(() => {
    if (!valeurs.manuel) return;
    setLibres(null);
    api.get(`ambassadeurs/codes-disponibles/?manuel=${valeurs.manuel}`).then((r) => setLibres(r.data))
      .catch((err) => setLibres({ erreur: erreurApi(err, "Impossible de charger les codes libres de ce manuel.") }));
  }, [valeurs.manuel]);
  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setMessage("");
    const corps = valeurs.mode === "codes"
      ? { ambassadeur: valeurs.ambassadeur, codes: valeurs.codes, note: valeurs.note }
      : { ambassadeur: valeurs.ambassadeur, manuel: valeurs.manuel, quantite: valeurs.quantite, note: valeurs.note };
    try { const r = await api.post("ambassadeurs/dotations/", corps); onSaved(r.data); } catch (err) { setMessage(erreurApi(err)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre="Nouvelle liste de manuels confiés" onClose={onClose}>
      <form onSubmit={envoyer}>
        <div className="qc-form">
          <label className="qc-full">Brand Ambassador<select required value={valeurs.ambassadeur} onChange={maj("ambassadeur")}>
            {ambassadeurs.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}</select></label>
          <label className="qc-full">Méthode<select value={valeurs.mode} onChange={maj("mode")}>
            <option value="quantite">Prendre des codes libres d’un manuel</option><option value="codes">Saisir ou coller les codes</option></select></label>
          {valeurs.mode === "quantite" ? <>
            <label>Manuel<select value={valeurs.manuel} onChange={maj("manuel")}>{manuels.map((m) => <option key={m.id} value={m.id}>{m.titre}</option>)}</select></label>
            <label>Nombre d’exemplaires<input type="number" min="1" max="500" required value={valeurs.quantite} onChange={maj("quantite")} /></label>
            {libres?.erreur && <div className="qc-full"><Message texte={libres.erreur} /></div>}
            {libres && !libres.erreur && <div className="qc-full qc-foot">{libres.total} code(s) libre(s) (ni activé ni déjà confié) pour ce manuel.</div>}
          </> : (
            <label className="qc-full">Codes manuels (un par ligne ou séparés par des virgules)
              <textarea rows={6} required value={valeurs.codes} onChange={maj("codes")} placeholder="QL-PHY-TLE-…" /></label>
          )}
          <label className="qc-full">Note (facultatif)<input maxLength={300} value={valeurs.note} onChange={maj("note")} /></label>
        </div>
        <div className="qs-notice">Un code déjà activé par un élève ou déjà confié à un autre ambassadeur est refusé. La liste apparaît aussitôt chez l’ambassadeur et dans tous les espaces.</div>
        <Message texte={message} />
        <div className="qc-actions"><button className="qc-btn qc-btn--gold" type="submit" disabled={envoi}>{envoi ? "Enregistrement…" : "Établir la liste"}</button></div>
      </form>
    </Fenetre>
  );
}

function Dotations({ ambassadeurId, version }) {
  const [donnees, setDonnees] = useState(null);
  useEffect(() => {
    api.get(`ambassadeurs/dotations/${ambassadeurId ? `?ambassadeur=${ambassadeurId}` : ""}`).then((r) => setDonnees(r.data)).catch(() => setDonnees(false));
  }, [ambassadeurId, version]);
  return (
    <div className="qc-card">
      <h3>Listes établies</h3>
      {donnees === null ? <Chargement /> : donnees === false ? <div className="qc-empty">Chargement impossible.</div> : (
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Liste</th><th>Ambassadeur</th><th>Exemplaires</th><th>Établie par</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {donnees.dotations.map((d) => (
                <tr key={d.id}><td><strong>{d.numero}</strong>{d.note && <small>{d.note}</small>}</td><td>{d.ambassadeur.nom}</td><td>{d.exemplaires}</td>
                  <td>{d.cree_par || "—"}</td><td>{dateHeure(d.created_at)}</td>
                  <td className="qa-actions-ligne"><BoutonsDocument lien url={`ambassadeurs/dotations/${d.id}/pdf/`} nom={`${d.numero}.pdf`} /></td></tr>
              ))}
              {!donnees.dotations.length && <tr><td colSpan={6} className="qc-empty">Aucune liste établie.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ComptaAmbassadeurs({ onChange }) {
  const [reversements, setReversements] = useState(null);
  const [mobiles, setMobiles] = useState(null);
  const charger = useCallback(() => {
    api.get("ambassadeur/reversements/?statut=soumis").then((r) => setReversements(r.data.reversements)).catch(() => setReversements([]));
    api.get("ambassadeurs/versements-a-rapprocher/").then((r) => setMobiles(r.data.versements)).catch(() => setMobiles([]));
  }, []);
  useEffect(charger, [charger]);
  const decider = (r, decision) => {
    const motif = decision === "rejeter" ? window.prompt("Motif du rejet :") : "";
    if (decision === "rejeter" && !motif) return;
    api.post(`ambassadeur/reversements/${r.id}/decision/`, { decision, motif }).then(() => { charger(); onChange(); })
      .catch((err) => qlAlert(erreurApi(err), "error"));
  };
  const rapprocher = (v) => api.post(`ambassadeur/versements/${v.id}/rapprocher/`).then(() => { charger(); onChange(); })
    .catch((err) => qlAlert(erreurApi(err), "error"));
  return (
    <div className="qc-cols qc-cols--half">
      <div className="qc-card">
        <h3>Bordereaux à valider</h3>
        {reversements === null ? <Chargement /> : <TableReversements reversements={reversements} onDecision={decider} />}
        <div className="qc-foot">Un bordereau validé entre dans les revenus (« Vente de manuels »).</div>
      </div>
      <div className="qc-card">
        <h3>Paiements Mobile Money à rapprocher</h3>
        {mobiles === null ? <Chargement /> : mobiles.length === 0 ? <div className="qc-empty">Rien à rapprocher.</div> : mobiles.map((v) => (
          <div key={v.id} className="qs-item"><span className="qs-dot" /><div style={{ flex: 1 }}><strong>{v.numero_recu} · {fcfa(v.montant)}</strong>
            <small>{v.mode_label} · réf. {v.reference} · {v.eleve} · {v.ambassadeur}</small></div>
            <button className="qc-btn qc-btn--sm" onClick={() => rapprocher(v)}>Rapprocher</button></div>
        ))}
      </div>
    </div>
  );
}

export function GestionAmbassadeursPage() {
  const [donnees, setDonnees] = useState(null);
  const [choisi, setChoisi] = useState(null);
  const [form, setForm] = useState(false);
  const [version, setVersion] = useState(0);
  const charger = useCallback(() => { api.get("ambassadeurs/").then((r) => setDonnees(r.data)).catch(() => setDonnees(false)); }, []);
  useEffect(charger, [charger, version]);
  const rafraichir = () => setVersion((v) => v + 1);
  if (donnees === null) return <div className="qc qs qa"><Chargement /></div>;
  if (donnees === false) return <div className="qc qs qa"><div className="qc-empty">Accès impossible.</div></div>;
  const amb = donnees.ambassadeurs.find((a) => a.id === choisi);
  return (
    <div className="qc qs qa">
      <Entete titre="Brand Ambassadors" sousTitre="Listes des manuels confiés (avec leurs codes), ventes et versements de chaque ambassadeur.">
        <button className="qc-btn qc-btn--gold" disabled={!donnees.ambassadeurs.length} onClick={() => setForm(true)}>＋ Nouvelle liste de manuels</button>
      </Entete>
      {!donnees.ambassadeurs.length && <div className="qs-notice">Aucun Brand Ambassador inscrit. Le CEO ou un administrateur crée un code dans « Codes Ambassadeurs » ; l’ambassadeur s’inscrit avec ce code.</div>}
      {donnees.finances && <ComptaAmbassadeurs onChange={rafraichir} />}
      <div className="qc-card" style={{ marginTop: 16 }}>
        <h3>Ambassadeurs</h3>
        <div className="qc-table-wrap">
          <table className="qc-table">
            <thead><tr><th>Ambassadeur</th><th>Reçus</th><th>Disponibles</th><th>Vendus</th>
              {donnees.finances && <><th>Total versé par les élèves</th><th>Reversé (validé)</th><th>À reverser</th></>}<th></th></tr></thead>
            <tbody>
              {donnees.ambassadeurs.map((a) => (
                <tr key={a.id} className={a.id === choisi ? "qa-choisi" : ""}>
                  <td><strong>{a.nom}</strong>{!a.actif && <small>Compte inactif</small>}</td>
                  <td>{a.recus}</td><td>{a.par_etat.disponible}</td><td>{a.vendus}</td>
                  {donnees.finances && <><td>{fcfa(a.encaisse)}</td><td>{fcfa(a.reverse_valide)}</td><td>{fcfa(a.a_reverser)}</td></>}
                  <td><button className="qc-link" onClick={() => setChoisi(a.id === choisi ? null : a.id)}>{a.id === choisi ? "Fermer" : "Ouvrir"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {amb && <div className="qa-detail">
        <StockAmbassadeur key={`s${amb.id}-${version}`} ambassadeurId={amb.id} gestion ambassadeurs={donnees.ambassadeurs} onChange={rafraichir} />
        {donnees.finances && <RegistreVentes key={`v${amb.id}-${version}`} ambassadeurId={amb.id} comptable titre={`Manuels vendus par ${amb.nom}`} />}
      </div>}
      <Dotations ambassadeurId={amb?.id} version={version} />
      {form && <FormDotation ambassadeurs={donnees.ambassadeurs.filter((a) => a.actif)} manuels={donnees.manuels} ambassadeurInitial={choisi}
        onClose={() => setForm(false)} onSaved={(d) => { setForm(false); qlAlert(`Liste ${d.numero} établie : ${d.exemplaires} exemplaire(s).`, "success"); rafraichir(); }} />}
    </div>
  );
}
